// Classics service — the in-app "app store for free books".
//
// A build-time script (scripts/build-classics-catalog.mjs) downloads the
// most popular public-domain classics from Project Gutenberg into
// public/classics/ (books/<id>.epub + covers/<id>.jpg) and writes
// catalog.json. This service fetches that self-hosted catalog and moves
// books in and out of the local library through the normal import/delete
// pipeline — so a classic behaves exactly like any imported EPUB once
// it's on the shelf (covers, positions, cloud mirroring, highlights).
//
// Library membership is keyed on fileName: every classic is imported as
// "<gutenberg-id>.epub" (mirroring the STARTER_FILE_NAMES pattern in
// starterLibraryService). That makes "is it in the library?" a cheap,
// deterministic lookup with no hashing of undownloaded files, and it
// stays correct across the add -> remove -> add-again cycle.

import { db } from "./db";
import type { Book } from "@/types/book";

export interface ClassicEntry {
  /** Project Gutenberg book id. */
  id: number;
  title: string;
  author: string;
  /** Up to 3 short subject labels for filter chips. */
  subjects: string[];
  /** Path relative to public/classics/, e.g. "books/1342.epub". */
  file: string;
  /** Path relative to public/classics/, e.g. "covers/1342.jpg" — or null. */
  cover: string | null;
  sizeBytes: number;
  /** ISO 639-1 code for the multilingual "in the original" editions
   *  (e.g. "de", "ja"). Absent on the regular English catalog. */
  lang?: string;
  /** Human label for the language, e.g. "Deutsch / German". */
  langLabel?: string;
  /** Title in the original language. For these editions the Gutenberg
   *  title is already original-language; this is the curated short form. */
  originalTitle?: string;
}

/** Resolve a classics asset relative to the deploy base, so it works at /
 *  in dev and under /pagevoice/ in production. Same pattern as
 *  starterUrl() in starterLibraryService. */
export function classicsUrl(file: string): string {
  const base = (import.meta.env.BASE_URL as string) || "/";
  return `${base.endsWith("/") ? base : base + "/"}classics/${file}`;
}

/** The fileName a classic is imported under — the library-membership key. */
export function classicFileName(entry: Pick<ClassicEntry, "id">): string {
  return `${entry.id}.epub`;
}

/** Fetch the self-hosted catalog. Throws on network/deployment problems so
 *  the view can render a real error state instead of an empty store. */
export async function fetchClassicsCatalog(): Promise<ClassicEntry[]> {
  const res = await fetch(classicsUrl("catalog.json"));
  if (!res.ok) {
    throw new Error(`Couldn't load the classics catalog (HTTP ${res.status}).`);
  }
  const entries = (await res.json()) as ClassicEntry[];
  if (!Array.isArray(entries)) {
    throw new Error("Classics catalog is malformed.");
  }
  return entries.filter((e) => e && typeof e.id === "number" && !!e.file);
}

/** Find the local library row for a classic, if it's been added.
 *  Matched by the deterministic fileName "<id>.epub". */
export async function findClassicInLibrary(
  entry: Pick<ClassicEntry, "id">,
): Promise<Book | undefined> {
  const fileName = classicFileName(entry);
  const books = await db.books.toArray();
  return books.find((b) => b.fileName === fileName);
}

/** Download a classic's EPUB (self-hosted, relative URL) and import it
 *  through the normal pipeline. Returns the new Book. No-ops (returning
 *  the existing row) if the classic is already on the shelf, so a double
 *  tap can't duplicate it. */
export async function addClassic(entry: ClassicEntry): Promise<Book> {
  const existing = await findClassicInLibrary(entry);
  if (existing) return existing;

  const res = await fetch(classicsUrl(entry.file));
  if (!res.ok) {
    throw new Error(`Download failed for “${entry.title}” (HTTP ${res.status}).`);
  }
  const blob = await res.blob();
  const file = new File([blob], classicFileName(entry), {
    type: "application/epub+zip",
  });
  // Lazy import mirrors starterLibraryService and avoids a module-load
  // cycle with bookStorageService.
  const { importEpub } = await import("./bookStorageService");
  return importEpub(file);
}

/** Remove a previously-added classic from the library via the standard
 *  delete path (which also handles cloud tombstones on the full build).
 *  Silently no-ops when the book isn't on the shelf. */
export async function removeClassic(
  entry: Pick<ClassicEntry, "id">,
): Promise<void> {
  const book = await findClassicInLibrary(entry);
  if (!book) return;
  const { deleteBook } = await import("./bookStorageService");
  await deleteBook(book.id);
}
